const length=v=>Math.hypot(v[0],v[1],v[2]);
const clamp=(value,low,high)=>Math.max(low,Math.min(high,value));
const identity=()=>new Float64Array([1,0,0,0,1,0,0,0,1]);
const aMatrix=new Float64Array(9),bMatrix=new Float64Array(9);
function axisScale(out,axis,parallel,perpendicular){
  for(let r=0;r<3;r++)for(let c=0;c<3;c++)out[r*3+c]=(r===c?perpendicular:0)+(parallel-perpendicular)*axis[r]*axis[c];
}
function multiply(out,a,b){
  for(let r=0;r<3;r++)for(let c=0;c<3;c++)out[r*3+c]=a[r*3]*b[c]+a[r*3+1]*b[c+3]+a[r*3+2]*b[c+6];
}

// Centers, sizes, pointer deltas and sampled vertices all use toy-local units.
// Pieces retain their released positions; bonds describe current contacts,
// never springs back to the original arrangement.
export class StickyPieces {
  constructor({centers,sizes,reach}={}){
    if(!centers?.length||sizes?.length!==centers.length)throw new TypeError('Each piece needs a center and size');
    this.centers=centers.map(p=>[...p]);this.sizes=sizes.map(s=>[...s]);
    this.unit=sizes.reduce((sum,s)=>sum+Math.max(...s),0)/sizes.length;
    this.reach=reach??this.unit*3.2;
    this.attachGap=this.unit*.065;this.breakGap=this.unit*.45;
    this.reset();
  }

  reset(){
    const count=this.centers.length;
    this.positions=Array.from({length:count},()=>[0,0,0]);
    this.velocities=Array.from({length:count},()=>[0,0,0]);
    this.transforms=Array.from({length:count},identity);
    this.normalTransforms=Array.from({length:count},identity);
    this.pressures=new Float64Array(count);this.pressureTargets=new Float64Array(count);
    this.strains=new Float64Array(count);this.strainTargets=new Float64Array(count);
    this.flows=new Float64Array(count);this.flowTargets=new Float64Array(count);
    this.flowAxes=Array.from({length:count},()=>[1,0,0]);this.flowNormals=Array.from({length:count},()=>[0,1,0]);
    this.directions=Array.from({length:count},()=>[1,0,0]);
    this.pressDirections=Array.from({length:count},()=>[0,0,1]);
    this.held=null;this.origin=[0,0,0];this.target=[0,0,0];this.activity=0;this.pressureInput=0;
    this.bonds=[];
    for(let a=0;a<count;a++)for(let b=a+1;b<count;b++){
      const d=this.separation(a,b),distance=length(d),contact=this.contactDistance(a,b,d,distance);
      this.bonds.push({a,b,distance,attached:distance<=contact*1.12,contactTime:0,cooldown:0,fade:0,tearTime:0,restDelta:[...d],axis:distance?d.map(n=>n/distance):[1,0,0]});
    }
  }

  grab(index){
    if(!Number.isInteger(index)||index<0||index>=this.centers.length)return;
    this.release();this.held=index;this.origin=[...this.positions[index]];this.target=[...this.origin];
    this.velocities[index].fill(0);this.activity=1;
  }

  move(delta){
    if(this.held===null||delta.length!==3||!delta.every(Number.isFinite))return;
    for(let axis=0;axis<3;axis++)this.target[axis]=this.origin[axis]+delta[axis];
    this.limit(this.target);
    const distance=length(delta);
    if(distance>1e-8)for(let axis=0;axis<3;axis++)this.directions[this.held][axis]=delta[axis]/distance;
    this.activity=1;
  }

  // Pressure is normalized 0..1. The outward normal can be reused by caller.
  press(depth,normal=[0,0,1]){
    if(this.held===null||!Number.isFinite(depth))return;
    this.pressureInput=clamp(depth,0,1);
    const magnitude=length(normal);
    if(Number.isFinite(magnitude)&&magnitude>1e-8)for(let axis=0;axis<3;axis++)this.pressDirections[this.held][axis]=normal[axis]/magnitude;
    this.activity=1;
  }

  release(){
    if(this.held!==null){this.pressureTargets[this.held]=0;this.strainTargets[this.held]=0;this.activity=1;}
    this.held=null;this.pressureInput=0;
  }

  offset(index){return [...this.positions[index]];}
  isAttached(a,b){return this.bonds.some(bond=>bond.attached&&((bond.a===a&&bond.b===b)||(bond.a===b&&bond.b===a)));}

  bondExtension(bond,target=false){
    const d=this.separation(bond.a,bond.b,target),rest=bond.restDelta,span=length(rest)||1;
    let axial=0,squared=0;
    for(let a=0;a<3;a++){const delta=d[a]-rest[a];axial+=delta*rest[a]/span;squared+=delta*delta;}
    // Sliding sideways also stretches sticky contact, whereas compression
    // should not turn into tensile strain.
    return Math.hypot(Math.max(0,axial),Math.sqrt(Math.max(0,squared-axial*axial))*.85);
  }

  bridges(){
    const result=[];
    for(const bond of this.bonds){
      if(!bond.attached&&bond.fade<=0)continue;
      const {a,b}=bond,d=bond.attached?this.separation(a,b):bond.axis,distance=length(d);
      const axis=distance>1e-8?d.map(n=>n/distance):[1,0,0];
      const anchor=(index,sign)=>{
        const size=this.sizes[index],center=this.centers[index];
        const support=Math.hypot(...axis.map((n,i)=>n*size[i]*.5))||1;
        const rest=center.map((n,i)=>n+sign*axis[i]*(size[i]*.5)**2/support*.80);
        return this.sample(index,...rest);
      };
      const tension=clamp(this.bondExtension(bond)/this.breakGap,0,1);
      result.push({a,b,start:anchor(a,1),end:anchor(b,-1),radius:this.unit*.18,verticalScale:.45,tension,attached:bond.attached,fade:bond.attached?1:bond.fade,retract:bond.attached?0:1-bond.fade});
    }
    return result;
  }

  limit(position){
    const distance=length(position);
    if(distance>this.reach){for(let axis=0;axis<3;axis++)position[axis]*=this.reach/distance;return true;}
    return false;
  }

  separation(a,b,heldTarget=false){
    const pa=heldTarget&&this.held===a?this.target:this.positions[a],pb=heldTarget&&this.held===b?this.target:this.positions[b];
    return [this.centers[b][0]+pb[0]-this.centers[a][0]-pa[0],this.centers[b][1]+pb[1]-this.centers[a][1]-pa[1],this.centers[b][2]+pb[2]-this.centers[a][2]-pa[2]];
  }

  contactDistance(a,b,d,distance){
    if(distance<1e-8)return (this.sizes[a][0]+this.sizes[b][0])*.5;
    let ra=0,rb=0;
    for(let axis=0;axis<3;axis++){
      const component=d[axis]/distance;
      ra+=(component*this.sizes[a][axis]*.5)**2;rb+=(component*this.sizes[b][axis]*.5)**2;
    }
    return Math.sqrt(ra)+Math.sqrt(rb);
  }

  step(dt){
    if(!Number.isFinite(dt)||dt<=0)return;
    const elapsed=Math.min(dt,.05),steps=Math.ceil(elapsed*120),h=elapsed/steps;
    for(let step=0;step<steps;step++){
      for(const bond of this.bonds){
        bond.cooldown=Math.max(0,bond.cooldown-h);
        if(!bond.attached)bond.fade=Math.max(0,bond.fade-h/.24);
        const extension=this.bondExtension(bond);
        bond.tearTime=bond.attached&&extension>this.breakGap*.65?bond.tearTime+h:Math.max(0,bond.tearTime-h*2);
        if(bond.attached&&(extension>this.breakGap||bond.tearTime>.20)){
          bond.attached=false;bond.contactTime=0;bond.cooldown=.28;bond.fade=1;
        }
      }
      this.strainTargets.fill(0);this.pressureTargets.fill(0);
      let heldAttached=false;
      for(const bond of this.bonds)if(bond.attached){
        const d=this.separation(bond.a,bond.b),distance=length(d),extension=this.bondExtension(bond);
        if(distance>1e-8)bond.axis=d.map(n=>n/distance);
        for(const index of [bond.a,bond.b]){
          const held=index===this.held;
          let resistance=extension;
          if(held){heldAttached=true;resistance=Math.max(resistance,this.bondExtension(bond,true));}
          const strain=resistance<=this.unit*.0005?0:clamp(resistance/this.unit*(held?1.5:1.1),0,.48);
          if(strain>this.strainTargets[index]){
            this.strainTargets[index]=strain<.0001?0:strain;
            if(!held)this.directions[index]=[...bond.axis];
          }
        }
      }
      // Free translation cannot keep inflating a detached piece. Only a bond
      // supplies tensile resistance; detached finger pressure stays light.
      if(this.held!==null)this.pressureTargets[this.held]=this.pressureInput*(heldAttached?1:.15);
      for(let i=0;i<this.positions.length;i++){
        const p=this.positions[i],v=this.velocities[i];
        if(i===this.held){
          const omega=19,decay=Math.exp(-omega*h);
          for(let axis=0;axis<3;axis++){
            const error=p[axis]-this.target[axis],term=v[axis]+omega*error;
            p[axis]=this.target[axis]+(error+term*h)*decay;
            v[axis]=(v[axis]-omega*term*h)*decay;
          }
        }else{
          const decay=Math.exp(-12*h);
          for(let axis=0;axis<3;axis++){p[axis]+=v[axis]*(1-decay)/12;v[axis]*=decay;}
        }
      }
      for(const bond of this.bonds){
        const {a,b}=bond,d=this.separation(a,b);let distance=length(d);
        const direction=distance<1e-8?[1,0,0]:d.map(value=>value/distance);
        const contact=this.contactDistance(a,b,direction,1);
        const minimum=Math.min(contact*.86,bond.attached?bond.distance:Infinity);
        if(distance<minimum){
          const wa=a===this.held?.08:1,wb=b===this.held?.08:1,correction=minimum-distance;
          for(let axis=0;axis<3;axis++){
            this.positions[a][axis]-=direction[axis]*correction*wa/(wa+wb);
            this.positions[b][axis]+=direction[axis]*correction*wb/(wa+wb);
          }
          distance=minimum;
        }
        if(bond.attached){
          if(this.held===null){
            const relax=1-Math.exp(-5*h);
            for(let axis=0;axis<3;axis++)bond.restDelta[axis]+=(direction[axis]*bond.distance-bond.restDelta[axis])*relax;
          }
          const error=distance-bond.distance;
          if(Math.abs(error)>this.unit*.0003){
            const wa=a===this.held?.75:b===this.held?.25:.5,correction=error*(1-Math.exp(-3*h));
            for(let axis=0;axis<3;axis++){
              this.positions[a][axis]+=direction[axis]*correction*wa;
              this.positions[b][axis]-=direction[axis]*correction*(1-wa);
            }
          }
        }else if(bond.cooldown===0){
          const va=this.velocities[a],vb=this.velocities[b],speed=Math.hypot(va[0]-vb[0],va[1]-vb[1],va[2]-vb[2]);
          if(distance<=contact+this.attachGap&&speed<this.unit*1.2){
            bond.contactTime+=h;
            if(bond.contactTime>=.16){bond.attached=true;bond.distance=distance;bond.contactTime=0;bond.fade=0;bond.tearTime=0;bond.axis=[...direction];bond.restDelta=this.separation(a,b);}
          }else bond.contactTime=0;
        }
      }
      for(let i=0;i<this.positions.length;i++){
        if(this.limit(this.positions[i]))this.velocities[i].fill(0);
        if(length(this.velocities[i])<this.unit*.0015)this.velocities[i].fill(0);
        const pressureRate=i===this.held?11:4.2,strainRate=i===this.held?10:7;
        this.pressures[i]+=(this.pressureTargets[i]-this.pressures[i])*(1-Math.exp(-pressureRate*h));
        this.strains[i]+=(this.strainTargets[i]-this.strains[i])*(1-Math.exp(-strainRate*h));
        const lag=i===this.held?Math.hypot(...this.positions[i].map((n,a)=>n-this.target[a]))/this.unit:0;
        this.flowTargets[i]=this.unit*clamp(this.strainTargets[i]*.65+lag*.35,0,.32);
        this.flows[i]+=(this.flowTargets[i]-this.flows[i])*(1-Math.exp(-7*h));
        if(this.flowTargets[i]===0&&this.flows[i]<this.unit*.0001)this.flows[i]=0;
        if(this.pressures[i]<.0001&&this.pressureTargets[i]===0)this.pressures[i]=0;
        if(this.strains[i]<.0001&&this.strainTargets[i]===0)this.strains[i]=0;
      }
    }
    this.updateTransforms();
    let activity=0;
    for(let i=0;i<this.positions.length;i++){
      activity=Math.max(activity,length(this.velocities[i])/this.unit,Math.abs(this.pressures[i]-this.pressureTargets[i]),Math.abs(this.strains[i]-this.strainTargets[i]),Math.abs(this.flows[i]-this.flowTargets[i])/this.unit);
      if(i===this.held)activity=Math.max(activity,Math.hypot(...this.positions[i].map((v,a)=>v-this.target[a]))/this.unit);
    }
    for(const bond of this.bonds){
      if(bond.cooldown>0||bond.contactTime>0||bond.fade>0)activity=Math.max(activity,.01);
      if(bond.attached){const error=Math.abs(length(this.separation(bond.a,bond.b))-bond.distance);if(error>this.unit*.0003)activity=Math.max(activity,error/this.unit);}
    }
    this.activity=activity<.0001?0:activity;
  }

  updateTransforms(){
    for(let i=0;i<this.positions.length;i++){
      const squash=1-.68*this.pressures[i],stretch=1+this.strains[i];
      const flowNormal=this.flowNormals[i],flowAxis=this.flowAxes[i],direction=this.directions[i];
      flowNormal[0]=0;flowNormal[1]=Math.abs(direction[1])<.8?1:0;flowNormal[2]=flowNormal[1]?0:1;
      const dot=direction[0]*flowNormal[0]+direction[1]*flowNormal[1]+direction[2]*flowNormal[2];
      for(let a=0;a<3;a++)flowAxis[a]=direction[a]-dot*flowNormal[a];
      const flowLength=length(flowAxis)||1;
      for(let a=0;a<3;a++)flowAxis[a]/=flowLength;
      axisScale(aMatrix,this.pressDirections[i],squash,1/Math.sqrt(squash));
      axisScale(bMatrix,this.directions[i],stretch,1/Math.sqrt(stretch));
      multiply(this.transforms[i],aMatrix,bMatrix);
      // For M=pressure*stretch, inverse-transpose has the same factor order.
      axisScale(aMatrix,this.pressDirections[i],1/squash,Math.sqrt(squash));
      axisScale(bMatrix,this.directions[i],1/stretch,Math.sqrt(stretch));
      multiply(this.normalTransforms[i],aMatrix,bMatrix);
    }
  }

  sample(index,x,y,z,out=[0,0,0]){
    const center=this.centers[index],offset=this.positions[index],m=this.transforms[index];
    const dx=x-center[0],dy=y-center[1],dz=z-center[2];
    out[0]=center[0]+offset[0]+m[0]*dx+m[1]*dy+m[2]*dz;
    out[1]=center[1]+offset[1]+m[3]*dx+m[4]*dy+m[5]*dz;
    out[2]=center[2]+offset[2]+m[6]*dx+m[7]*dy+m[8]*dz;
    const n=this.flowNormals[index],axis=this.flowAxes[index];
    const projected=n[0]*(out[0]-center[0]-offset[0])+n[1]*(out[1]-center[1]-offset[1])+n[2]*(out[2]-center[2]-offset[2]);
    const shear=this.flows[index]*Math.tanh(projected/(this.unit*.45));
    for(let a=0;a<3;a++)out[a]+=axis[a]*shear;
    return out;
  }

  normal(index,x,y,z,out=[0,0,0],restX=this.centers[index][0],restY=this.centers[index][1],restZ=this.centers[index][2]){
    const m=this.normalTransforms[index],transform=this.transforms[index],center=this.centers[index];
    let nx=m[0]*x+m[1]*y+m[2]*z,ny=m[3]*x+m[4]*y+m[5]*z,nz=m[6]*x+m[7]*y+m[8]*z;
    const dx=restX-center[0],dy=restY-center[1],dz=restZ-center[2],n=this.flowNormals[index],axis=this.flowAxes[index];
    const projected=n[0]*(transform[0]*dx+transform[1]*dy+transform[2]*dz)+n[1]*(transform[3]*dx+transform[4]*dy+transform[5]*dz)+n[2]*(transform[6]*dx+transform[7]*dy+transform[8]*dz);
    // q += axis*f(normal·q) has determinant one because axis·normal=0.
    // Its exact inverse transpose is I - f' * normal * axis^T.
    const tanh=Math.tanh(projected/(this.unit*.45)),derivative=this.flows[index]/(this.unit*.45)*(1-tanh*tanh);
    const correction=derivative*(axis[0]*nx+axis[1]*ny+axis[2]*nz);
    nx-=n[0]*correction;ny-=n[1]*correction;nz-=n[2]*correction;
    const magnitude=Math.hypot(nx,ny,nz)||1;
    out[0]=nx/magnitude;out[1]=ny/magnitude;out[2]=nz/magnitude;return out;
  }
}
